import type { ReactNode, Ref } from 'react';
import React, { useCallback } from 'react';
import type { LiveSearchProps } from '../form';
import { LiveSearch } from '../form';
import type { icModel } from 'generated-ts-common';
import { BlankState, NameCircle } from '../basic';
import { HBox, VBox } from '../layout';
import { intl } from '../../lib';
import { isEmptyString } from '../../util';

type HealthCareProvider = icModel.saf.infrastructure.model.hpd.view.DPOHealthCareProvider;

/**
 * Props for a {@link HPDLiveSearch} component.
 */
export type HPDLiveSearchProps = Omit<
  LiveSearchProps<HealthCareProvider>,
  'label' | 'renderEmptyItems' | 'itemToKey' | 'itemToString' | 'menuClassName'
> &
  Partial<Pick<LiveSearchProps<HealthCareProvider>, 'label'>>;

/**
 * Function that renders each item of the result list.
 *
 * @param item a {@link HealthCareProvider}
 * @return the created {@link ReactNode}
 * @constructor
 */
function renderLiveSearchItem(item: HealthCareProvider): ReactNode {
  return (
    <HBox className="hpd-live-search-item">
      <NameCircle firstName={item.givenName} lastName={item.familyName} />
      <VBox className="hpd-live-search-item-content">
        <div className="displayName">{item.displayName}</div>
        <div className="specialty">
          {(item.speciality ?? item.type)?.join(', ')}
        </div>
      </VBox>
    </HBox>
  );
}

/**
 * {@link LiveSearch} component specifically for a search for {@link HealthCareProvider}s.
 *
 * @param properties a {@link HPDLiveSearchProps} object
 * @return the created {@link React.JSX.Element}.
 * @constructor
 */
export const HPDLiveSearch = function HPDLiveSearch({
  minQueryLength = 3,
  automaticallySelectSingleResult = true,
  renderItem = renderLiveSearchItem,
  label,
  ref,
  ...props
}: Readonly<HPDLiveSearchProps> & {
  ref?: Ref<HTMLInputElement>;
}): React.JSX.Element {
  const itemToString = useCallback(
    (item: HealthCareProvider) => item.displayName,
    []
  );
  const itemToKey = useCallback(
    (item: HealthCareProvider) => item.identifier,
    []
  );
  const renderEmptyItem = useCallback(
    () => (
      <BlankState
        iconName="person"
        description={intl('common.search.noentry')}
      />
    ),
    []
  );

  return (
    <LiveSearch<HealthCareProvider>
      {...props}
      ref={ref}
      menuClassName="ehs-hpd-live-search-menu"
      itemToKey={itemToKey}
      itemToString={itemToString}
      label={
        label ?? (isEmptyString(label) ? intl('view.button.search') : label)
      }
      renderItem={renderItem}
      renderEmptyItems={renderEmptyItem}
      automaticallySelectSingleResult={automaticallySelectSingleResult}
      minQueryLength={minQueryLength}
    />
  );
};
